import React from "react";
import { MAX_COMPETITORS } from "../utils/targetInputs";

/**
 * The main-domain + competitor block, shared by onboarding and project settings.
 *
 * Locking rules (enforced again on the server):
 *   • the main domain is write-once — editable only while unset
 *   • a competitor is write-once — a saved slot is read-only, an empty slot
 *     can still be filled in later
 */


/**
 * The full competitor list to submit: saved entries keep their slot (the server
 * rejects any payload that reorders or drops them), newly typed ones append.
 * Stops at the first gap so the list stays dense.
 */
export function mergeCompetitors(saved = [], local = []) {
  const out = [];
  for (let i = 0; i < MAX_COMPETITORS; i += 1) {
    const value = (saved[i] || local[i] || "").trim();
    if (!value) break;
    out.push(value);
  }
  return out;
}

function LockIcon({ size = 12, color = "var(--color-5F6567)" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="2.5" y="6" width="9" height="6.5" rx="1.2" stroke={color} strokeWidth="1.3" />
      <path d="M4.75 6V4.25a2.25 2.25 0 014.5 0V6" stroke={color} strokeWidth="1.3" />
    </svg>
  );
}

/** A saved, non-editable domain row. */
function LockedValue({ value, note }) {
  return (
    <div className="form__input locked__input" title={note}>
      <span className="txt__ellipsis">
        {value}
      </span>
      <span className="wrapper__locked fb__10 fw__700">
        <LockIcon />
        Locked
      </span>
    </div>
  );
}

export default function DomainFields({
  savedDomain = null,
  savedCompetitors = [],
  domain,
  competitors,
  onDomainChange,
  onCompetitorChange,
  disabled = false,
  errors = {},
  autoFocus = false,
}) {
  const domainLocked = !!savedDomain;
  const saved = savedCompetitors || [];

  return (
    <>
      {/* ── Main domain ─────────────────────────────────────────────────── */}
      <div className="wrapper__mainDomain mb__20">
        <label  className="form__label">
          Main Domain {!domainLocked && <span className="fc__E52F0F">*</span>}
        </label>
        <p className="fb__hint">
          The website this project analyses. Every agent run is limited to this
          domain and its competitors.{" "}
          {domainLocked ? (
            <strong className="fc__5F6567 fw__700">
              It cannot be changed — contact your administrator if it is wrong.
            </strong>
          ) : (
            <strong className="fc__5F6567 fw__700">
              This is permanent — it cannot be changed after saving.
            </strong>
          )}
        </p>
        {domainLocked ? (
          <LockedValue value={savedDomain} note="The main domain cannot be changed" />
        ) : (
          <>
            <input
              className={errors.domain ? "form__input error" : "form__input"}
              value={domain}
              onChange={(e) => onDomainChange(e.target.value)}
              placeholder="acme.com"
              disabled={disabled}
              autoComplete="off"
              spellCheck={false}
              autoFocus={autoFocus}
            />
            {errors.domain && (
              <div className="validateError">
                {errors.domain}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Competitors ─────────────────────────────────────────────────── */}
      <div className="wrapper__sec mb__20">
        <label className="form__label">
          Competitors{" "}
          <span className="fc__5F6567 fb__11 transform__none">
            (up to {MAX_COMPETITORS}, optional)
          </span>
        </label>
        <p className="fb__hint">
          Agents benchmark against these when a report calls for competitor
          comparison, and they become selectable targets when you run an agent.
          You can add them now or later — but <br /> {" "}
          <strong className="fc__5F6567 fw__700">
            once a competitor is saved it cannot be changed or removed.
          </strong>
        </p>

        <div className="wrapper__competitors">
          {Array.from({ length: MAX_COMPETITORS }).map((_, i) => {
            const savedValue = saved[i];
            const errorKey = `competitor${i}`;
            // A slot only opens up once every earlier slot has a value (saved
            // or just typed), so the stored list stays dense and append-only.
            const isNextOpenSlot = Array.from({ length: i }).every(
              (_, j) => (saved[j] || competitors[j] || "").trim() !== "",
            );
            return (
              <div key={i} className="competitors__col">
                {savedValue ? (
                  <LockedValue
                    value={savedValue}
                    note="This competitor cannot be changed"
                  />
                ) : (
                  <>
                    <input 
                      className={`form__input ${errors[errorKey] ? "error": ""} ${isNextOpenSlot ? "" : "dimmed"} `}
                      value={competitors[i] || ""}
                      onChange={(e) => onCompetitorChange(i, e.target.value)}
                      placeholder={
                        isNextOpenSlot
                          ? `Competitor ${i + 1} — e.g. rival${i + 1}.com`
                          : `Competitor ${i + 1} — fill the slot above first`
                      }
                      disabled={disabled || !isNextOpenSlot}
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {errors[errorKey] && (
                      <div className="validateError">
                        {errors[errorKey]}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {errors.competitors && (
          <div className="validateError mt__8">
            {errors.competitors}
          </div>
        )}
      </div>
    </>
  );
}
